import CircularProgress from '@/components/common/CircularProgress';
import { NOTIFICATION_TYPES } from './notificationTypes';
import { PwaSwitch } from './PwaSwitch';
import { usePushNotifications, type PushUiState } from './usePushNotifications';

/** Texto de apoyo bajo el interruptor general, según el estado de las push en este dispositivo. */
const STATUS_TEXT: Record<PushUiState, string> = {
  loading: 'Cargando…',
  working: 'Un momento…',
  on: 'Las notificaciones están activadas en este dispositivo.',
  off: 'Activalas para enterarte al instante de tus avisos de Psi Mammoliti.',
  denied:
    'Tenés las notificaciones bloqueadas. Habilitalas para esta app desde los ajustes de tu dispositivo y volvé a abrirla.',
  unsupported:
    'Este dispositivo no permite recibir notificaciones. En iPhone, instalá la app desde Safari (Compartir → Agregar a pantalla de inicio) y abrila desde ahí.',
  unconfigured: 'Las notificaciones todavía no están disponibles.',
  impersonated: 'Estás viendo la cuenta de otra persona: desde acá no se pueden activar las notificaciones.',
};

/** El interruptor solo se puede mover cuando hay algo para prender o apagar. */
const INTERACTIVE_STATES: PushUiState[] = ['on', 'off'];

/**
 * Notificaciones de la app mobile: el interruptor general de las push del dispositivo y, debajo, qué tipos de aviso quiere
 * cada psicóloga (ver `notificationTypes.ts`). Vive como una sección DENTRO de Perfil (`/profile/notificaciones`, con
 * `embedded`: sin título ni márgenes propios, los pone Perfil).
 *
 * El bloqueo de la app por notificaciones NO depende de esta pantalla (`PwaPushGate`): si las desactiva acá, la app se
 * bloquea y pide activarlas de nuevo.
 */
export function PwaNotificaciones({ embedded = false }: { embedded?: boolean }) {
  const { state, error, enable, disable } = usePushNotifications();
  const busy = state === 'loading' || state === 'working';
  const Wrapper = embedded ? 'div' : 'main';

  return (
    <Wrapper
      className={
        embedded
          ? 'flex w-full flex-col gap-5 text-left font-[Poppins,sans-serif]'
          : 'mx-auto flex w-full max-w-3xl flex-col gap-5 px-4 pb-6 pt-[max(1.25rem,env(safe-area-inset-top))] text-left font-[Poppins,sans-serif]'
      }
    >
      {embedded ? (
        <p className="m-0 text-sm text-[#455375]">Elegí cómo querés enterarte de lo que pasa en Psi Mammoliti.</p>
      ) : (
        <header className="flex flex-col gap-1">
          <h1 className="m-0 text-2xl font-semibold text-[#2d2049]">Notificaciones</h1>
          <p className="m-0 text-sm text-[#455375]">Elegí cómo querés enterarte de lo que pasa en Psi Mammoliti.</p>
        </header>
      )}

      <section aria-labelledby="push-general" className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1">
            <h2 id="push-general" className="m-0 text-base font-semibold text-[#2d2049]">
              Avisos en este dispositivo
            </h2>
            <p className="m-0 text-sm text-[#455375]">{STATUS_TEXT[state]}</p>
          </div>

          {busy ? (
            <CircularProgress sizeRem={1.75} label={state === 'loading' ? 'Cargando' : 'Un momento'} />
          ) : (
            <PwaSwitch
              label="Recibir notificaciones en este dispositivo"
              checked={state === 'on'}
              disabled={!INTERACTIVE_STATES.includes(state)}
              onChange={(next) => (next ? enable() : disable())}
            />
          )}
        </div>

        {error && (
          <p role="alert" className="m-0 text-sm text-coral">
            {error}
          </p>
        )}
      </section>

      {/* Aparece solo cuando se define algún tipo de aviso (ver notificationTypes.ts). */}
      {NOTIFICATION_TYPES.length > 0 && (
        <section aria-labelledby="push-tipos" className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm">
          <h2 id="push-tipos" className="m-0 text-base font-semibold text-[#2d2049]">
            Qué avisos querés recibir
          </h2>
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {NOTIFICATION_TYPES.map((type) => (
              <li key={type.key} className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-sm font-semibold text-[#2d2049]">{type.label}</span>
                  <span className="text-xs text-[#455375]">{type.description}</span>
                </div>
                {/* Conectar con la preferencia guardada en el backend cuando exista el primer tipo. */}
                <PwaSwitch label={type.label} checked disabled onChange={() => undefined} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </Wrapper>
  );
}
